import { ExitPromptType } from '@frame/ui-components/exit-prompt'

import { TrueworkJsFeedbackReason } from '@frame/lib/api/common/types'
import { ConnectorId, DirectTask, TrueworkJsStrategy } from '@frame/lib/api/tasks/types'
import { ItemKind } from '@frame/lib/hooks/useGetItems'
import { PartnerEventPayloadDynamic } from '@frame/lib/hooks/useMonitoring'

export enum EventSource {
  ARGYLE = 'argyle',
  FINICITY = 'finicity',
  INTERNAL_WIDGET = 'internal-widget',
  TRUV = 'truv',
}

export enum WidgetEventType {
  WIDGET_BOOTED = 'widget-booted',
  WIDGET_INIT_ERROR = 'widget-init-error',
  WIDGET_SUCCESS = 'widget-success',
  WIDGET_ERROR = 'widget-error',
  CLOSED_WIDGET = 'closed-widget',
}

export enum InternalEventType {
  LOGIN_SUCCEEDED = 'login-succeeded',
  LOGIN_FAILED = 'login-failed',
  CONSENT_VIEWED = 'consent-viewed',
  CONSENT_PROVIDED = 'consent-provided',
  ATTEMPTED_EXIT = 'attempted-exit',
  CONFIRMED_EXIT = 'confirmed-exit',
  CANCELLED_EXIT = 'cancelled-exit',
  FEEDBACK_SUBMITTED = 'feedback-submitted',
  RETURNED_FROM_FEEDBACK = 'returned-from-feedback',
  CLICKED_VIDEO_LINK = 'clicked-video-link',
  EMPLOYER_SEARCH_VIEWED = 'employer-search-viewed',
  PAYROLL_PLATFORM_SEARCH_VIEWED = 'payroll-platform-search-viewed',
  CLICKED_SEARCH_RESULT = 'clicked-search-result',
  COMPLETED_EMPLOYER_VERIFICATION_FLOW = 'completed-employer-verification-flow',
  FAILED_EMPLOYER_VERIFICATION_FLOW = 'failed-employer-verification-flow',
  EDITED_EMPLOYER = 'edited-employer',
  SHARED_EMPLOYER_DATA = 'shared-employer-data',
  INCOME_TYPE_SELECTED = 'income-type-selected',
  FILE_UPLOADED = 'file-uploaded',
  DOCUMENT_FLOW_COMPLETED = 'document-flow-completed',
  ADDED_EMPLOYER = 'added-employer',
  STARTED_EMPLOYER_VERIFICATION_FLOW = 'started-employer-verification-flow',
  EMPLOYER_HISTORY_VIEWED = 'employer-history-viewed',
  EMPLOYER_HISTORY_ADD_EMPLOYER = 'employer-history-add-employer',
  EXIT_VERIFICATION_VIEWED = 'exit-verification-viewed',
  EXIT_VERIFICATION_CANCEL = 'exit-verification-cancel',
  EXIT_VERIFICATION_SUBMIT_FINISH = 'exit-verification-submit-finish',
  EXIT_VERIFICATION_SUBMIT_FINISH_LATER = 'exit-verification-submit-finish-later',
  EXIT_VERIFICATION_SUBMIT_START_OVER = 'exit-verification-submit-start-over',
  BOOT_PARTNER_WIDGET = 'boot-partner-widget',
  EMPLOYER_QUERIED = 'employer-queried',
  PAYROLL_PLATFORM_QUERIED = 'payroll-platform-queried',
  BANK_STATED_INFO_PROVIDED = 'bank-stated-info-provided',
  BANK_CONNECT_TIMEOUT = 'bank-connect-timeout',
  BANK_WIDGET_FAILED_TO_LOAD = 'bank-widget-failed-to-load',
  BANK_INCOME_IDENTIFIED = 'bank-income-identified',
  IRS_TASK_SELECTED = 'irs-task-selected',
  IRS_TASK_SUBMITTED = 'irs-task-submitted',
  ATTEMPTED_TO_UPLOAD_UNSUPPORTED_IMAGE_TYPE = 'attempted-to-upload-unsupported-image-type',
  OFFER_LETTER_UPLOADED = 'offer-letter-uploaded',
}

export enum BlendEventType {
  INITIALIZED_MODAL = 'initialized_modal',
  CLOSED_MODAL = 'closed_modal',
  CONNECTED_ACCOUNT = 'connected_account',
  INTERNAL_ERROR = 'internal_error',
  LOGIN_ERROR = 'login_error',
  MODAL_INITIALIZATION_ERROR = 'modal_initialization_error',
}

type BrowserInfo = {
  browser: string
  os: string
  screen_size: string
  viewport_size: string
}

export type PartnerEventPayload = {
  event_name: string
  browser_info: BrowserInfo
  publishable_key: string
  instance_id?: string
  connect_session_token?: string
  truework_js_session_token?: string
  created_at: Date
  event_source: EventSource
  partner_event?: unknown
  truework_js_version: string
  truework_js_event_version: string
  employment_id?: string
  referrer?: string
}

type PartnerEventProperties = {
  name?: ConnectorId
  feedback?: TrueworkJsFeedbackReason | null
  other_feedback?: string
}

export type TaskState = Pick<DirectTask, 'id' | 'employer_name' | 'state' | 'strategy'>
type EmployerHistoryViewState = {
  can_add_employer: boolean
  tasks: TaskState[]
  bil_enabled_strategies: TrueworkJsStrategy[]
  index?: number
}

type ExitVerificationViewState = {
  current_pathname?: string
  exit_prompt_type?: ExitPromptType
}

type ItemState = {
  item_name: string
  item_id: string
}

export type UnifiedSearchViewState = {
  index: number
  matching_score?: string
  item_id?: string
  item_kind: ItemKind
  item_name?: string
  total_results: number
  query?: string // We won't have a query string to pull bank income streams data
  other_items: ItemState[]
}

type SearchViewState = {
  prefilled_employer?: TaskState
  prefilled_query?: string | null
}

type ViewState = {
  employer_view?: EmployerHistoryViewState
  exit_verification_view?: ExitVerificationViewState
  search_view?: UnifiedSearchViewState
  employer_search_view?: SearchViewState
  payroll_platform_search_view?: SearchViewState
}

type InternalEventProperties = {
  totalResults?: number
  clickedIndex?: number
  matchingScore?: string
  // Ignoring previous casing because it's vestigial
  scope?: EventScope
  views?: ViewState
  file_name?: string
  file_type?: string
}

export enum EventScope {
  SESSION = 'session',
  EMPLOYER = 'employer',
  PLATFORM = 'platform',
}
// Since we occasionally spread the event properties object into the monitor call,
// we need to prevent the event_name and event_source properties from being overwritten
type NoProperty<T, K extends keyof PartnerEventPayloadDynamic> = K extends keyof T ? never : T
export type EventProperties = NoProperty<
  {
    partner_event?: PartnerEventProperties
    internal_metadata?: InternalEventProperties
  },
  'event_name' | 'event_source'
>
